import { HttpClient, HttpHeaders } from '@angular/common/http';
import { Injectable } from '@angular/core';
import { decrypt, encrypt, getValueProperty, lastValueFrom, setValueProperty } from '../../helpers/functions.helpers';
import { ApplicationService } from '../application/application.service';
import { LinkupService } from '../link-up/linkup.service';
import { ScriptLoaderService } from './../application/script-loader.service';
import { CryptoVaultService } from './crypto-vault.service';
import { TokenService } from './token.service';

declare const grecaptcha: any;

export interface IEventValidate {

  data: {
    recaptcha_credentials: {
      token: string,
      validation_type: string
    }
  }
}

@Injectable({
  providedIn: 'root'
})
export class RecaptchaService {

  private _siteKey: string = '';
  private _projectId: string = 'solicitudes-virtuales-coe';
  private _channelId: string = '';
  private _application: string = '';
  private _tokenCaptcha: string = '';

  constructor(
    private readonly _scriptLoader: ScriptLoaderService,
    private readonly _http: HttpClient,
    private readonly _token: TokenService,
    private readonly _link: LinkupService,
    private readonly _crypto: CryptoVaultService
  ) { }


  async loadCredentials() {
    const settings = await this._link.settings;
    const contextUrls = await this._link.contextUrls;
    const useCryptoVault = getValueProperty(settings, "recaptcha.useCryptoVault", true);
    const configSiteKey = getValueProperty(settings, 'recaptcha.siteKey', false);
    const configProjectId = getValueProperty(settings, 'recaptcha.projectId', false);
    let response: any;
    if (configSiteKey && configProjectId) {
      response = {
        data: {
          site_key: configSiteKey,
          project_id: configProjectId
        }
      };
    } else {
      const URL = `${contextUrls.URLS.RECAPTCHA_CREDENTIALS}`;
      let options = await this.httpOptions();
      if (useCryptoVault) {
        const transaction_id = await this._token.transactionId;
        await this.initCryptoVault(this._crypto, transaction_id);
        const cryptoVaultSettings = getValueProperty(settings, 'request.RECAPTCHA_CREDENTIALS.cryptoVault', { fields: [], headers: [] });
        options.headers = new HttpHeaders(await encrypt(this._crypto, options.headers, getValueProperty(cryptoVaultSettings, "headers", []), true));
      }
      const req$ = this._http.get(URL, options);
      response = await lastValueFrom(req$);
      if (useCryptoVault && getValueProperty(response, 'data', false)) {
        let encryptData = getValueProperty(response, 'data', {});
        if (encryptData) {
          for (let property of Object.keys(encryptData)) {
            let decryptData = await decrypt(this._crypto, getValueProperty(encryptData, property, ''));
            setValueProperty(encryptData, property, decryptData);
          }
          setValueProperty(response, 'data', encryptData);
        }
      }
    }
    this.siteKey = getValueProperty(response, 'data.site_key');
    this.projectId = getValueProperty(response, 'data.project_id');
  }

  public get siteKey(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      if (!this._siteKey) {
        await this.loadCredentials();
      }
      resolve(this._siteKey);
    });

  }

  public set siteKey(value: any) {
    this._siteKey = value;
  }

  public get projectId(): Promise<string> {
    return new Promise(async (resolve, reject) => {
      if (!this._projectId) {
        await this.loadCredentials();
      }
      resolve(this._projectId);
    });
  }

  public set projectId(value: any) {
    this._projectId = value;
  }

  public get channelId(): string {
    return this._channelId;
  }

  public set channelId(value: string) {
    this._channelId = value;
  }

  public get application(): string {
    return this._application;
  }

  public set application(value: string) {
    this._application = value;
  }

  public get tokenCaptcha(): string {
    return this._tokenCaptcha;
  }

  public set tokenCaptcha(value: string) {
    this._tokenCaptcha = value;
  }

  async register(channel: string, application: string) {

    this.application = application;
    this.channelId = channel;
    const settings = await this._link.settings;
    const captchaURL = getValueProperty(settings, "recaptcha.urlLib", "https://www.google.com/recaptcha/enterprise.js");
    await this._scriptLoader.loadScript({
      name: 'recaptcha',
      src: `${captchaURL}?render=${await this.siteKey}`
    });
  }

  generateToken(options: any): Promise<string> {
    return new Promise((resolve) => {
      /** @todo ajustar implementacion*/
      //if(this._scriptLoader.isLoaded('recaptcha')){
      grecaptcha.enterprise.ready(async () => {
        this.tokenCaptcha = await grecaptcha.enterprise.execute(await this.siteKey, options);
        resolve(this._tokenCaptcha);
        // IMPORTANT: The 'token' that results from execute is an encrypted response sent by
        // reCAPTCHA Enterprise to the end user's browser.
        // This token must be validated by creating an assessment.
        // See https://cloud.google.com/recaptcha-enterprise/docs/create-assessment
      });
      //} 
    });
  }

  async isValid(action: string) {

    if (!this.tokenCaptcha) {
      return false;
    }

    try {
      let data: IEventValidate =
      {
        data: {
          recaptcha_credentials: {
            token: this.tokenCaptcha,
            validation_type: action
          }
        }
      };
      const settings = await this._link.settings;
      const contextUrls = await this._link.contextUrls;
      const useCryptoVault = getValueProperty(settings, "recaptcha.useCryptoVault", true);
      const URL = `${contextUrls.URLS.RECAPTCHA_SCORE}`;
      let options = await this.httpOptions();
      if (useCryptoVault) {
        const transaction_id = await this._token.transactionId;
        await this.initCryptoVault(this._crypto, transaction_id);
        const cryptoVaultSettings = getValueProperty(settings, 'request.RECAPTCHA_SCORE.cryptoVault', { fields: [], headers: [] });
        data = await encrypt(this._crypto, data, getValueProperty(cryptoVaultSettings, "fields", []));
        options.headers = new HttpHeaders(await encrypt(this._crypto, options.headers, getValueProperty(cryptoVaultSettings, "headers", []), true));
      }
      const req$ = this._http.post(URL, data, options);
      let response: any = await lastValueFrom(req$);
      if (useCryptoVault && getValueProperty(response, 'data.message', false)) {
        let encryptData = getValueProperty(response, 'data.message');
        if (encryptData) {
          response = await decrypt(this._crypto, encryptData);
        }
      }
      const validationStatus = getValueProperty(response, 'data.validation_status', false);
      const validationType = getValueProperty(response, 'data.validation_type', '');
      return validationStatus && validationType == action;
    } catch (error) {
      return false;
    }

  }

  /**
   * Permite construir de manera dinamica los encabezados para las peticiones al servicio
   */
  async httpOptions() {
    const { accessToken } = await this._token.accessToken;
    const transaction_id = await this._token.transactionId;
    const session_id = await this._token.sessionId;
    return {
      headers: new HttpHeaders()
        .set('Authorization', `Bearer ${accessToken}`)
        .set('token-client-id', `${getValueProperty(this._token, 'userData.document_number', '0')}_${getValueProperty(this._token, 'userData.document_number', '0')}`)
        .set('transaction_id', transaction_id)
        .set('application', this.application)
        .set('channel-id', this.channelId)
        .set('session_id', session_id)
        .set('terminal_id', '127.0.0.1')
        .set('timestamp', new Date().toISOString())
    };
  }

  async initCryptoVault(crypto: CryptoVaultService, transactionId: string) {
    await crypto.keyExchange(transactionId);
  }
}
